import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../../store';
import { IntakeTabValidationKey } from '../../store/slices/intakeSlice';
import { AddressSuggestionModal } from '../../pages/CreatePatientForm/AddressSuggestionModal';
import { FeatureFlag } from '../../constants/featureFlags';
import { client } from '../../services/api';
import { initializeEmrSelectionEndpointIntakeIntakeIdInitializeEmrSelectionPost } from '../../services/api/sdk.gen';
import {
  Alert,
  AlertDescription,
  Card,
  CardHeader,
  CardContent,
} from '@roger/ui-components';
import {
  AlertTriangle,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  CalendarDays,
  ExternalLink,
} from 'lucide-react';
import { useCreateEpisodeStepForm } from '../../hooks/useCreatePatientForm/useCreateEpisodeStepForm';
import { useKinnserScheduling } from '../../hooks/useCreatePatientForm/useKinnserScheduling';
import { useKinnserAutoAddFlow } from '../../hooks/useCreatePatientForm/useKinnserAutoAddFlow';
import { useRogerScheduling } from '../../hooks/useCreatePatientForm/useRogerScheduling';
import {
  computeAndSetIntakeTabValidation,
  validateSchedulingFromIntakeWithClinicData,
} from '../../hooks/useIntakeTabValidationOnLoad';
import { useSchedulingClinicData } from '../../hooks/useCreatePatientForm/useSchedulingClinicData';
import { useIntakeFormSchema } from '../../config/intakeFormSchema';
import { useIntake } from '../../hooks/useIntake';
import { useToast } from '../../hooks/useToastContext';
import { PatientCreateButton } from './PatientCreateButton';
import MarkCompletedButton from './MarkCompletedButton';
import { KinnserCreateEpisodeStepFormContent } from '../../pages/CreatePatientForm/KinnserCreateEpisodeStepFormContent';
import { RogerCreateEpisodeStepFormContent } from '../../pages/CreatePatientForm/RogerCreateEpisodeStepFormContent';
import type {
  KinnserCreatePatientResponse,
  RogerOnboardPatientResponse,
} from '../../services/api/types.gen';
import type { KinnserOnboardPatientResponse } from '../../hooks/useCreatePatientForm/useKinnserOnboarding';
import type { OnboardingFieldConflict } from '../../hooks/useCreatePatientForm/types';
import { shouldSuppressRogerOnboardingPushError } from '../../hooks/useCreatePatientForm/rogerOnboardingErrors';

interface CreateEpisodeStepProps {
  onComplete?: () => Promise<void>;
  onStatusChange?: () => void | Promise<void>;
  onOnboardingFieldConflict?: (
    conflict: OnboardingFieldConflict
  ) => void | Promise<void>;
}

type DisplayCreateResult =
  | KinnserCreatePatientResponse
  | KinnserOnboardPatientResponse
  | RogerOnboardPatientResponse
  | null;

const CreateEpisodeStep: React.FC<CreateEpisodeStepProps> = ({
  onComplete,
  onStatusChange,
  onOnboardingFieldConflict,
}) => {
  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
  const user = useStore(state => state.user);
  const { confirmTabStatus } = useIntake();
  const { error: showError } = useToast();
  const intakeTabValidation = useStore(state => state.intakeTabValidation);
  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);
  const { schema: intakeFormSchema } = useIntakeFormSchema();
  const hasSchedulingFeatureFlag = hasFeatureFlag(
    FeatureFlag.INTAKE_SCHEDULING_SUPPORT
  );
  const hasKinnserOption = user?.agency?.is_kinnser_enabled === true;

  const {
    // Form
    control,
    register,
    errors,
    setValue,
    getValues,
    handleFieldBlur,

    // Watched values
    hhCahpsOptOut,
    kinnserLanguageKey,
    pushToRoger,
    pushToKinnser,

    // Save status
    saveStatus,
    saveToServer,

    // Clinics
    clinics,
    clinicData,
    loadingClinics,
    loadingClinicData,
    insuranceOptions,

    // Roger physician options
    rogerPhysicianOptions,
    loadingPhysicians,

    // Form validation
    isValid,

    // Create Episode Generic
    createResult,
    creatingInEMR,
    isUsingOnboarding,

    // Kinnser creation/onboarding
    handleCreateInKinnser,

    // Roger creation
    handleCreateInRoger,
    rogerCreateResult,

    // Intake data
    selectedIntake,

    // Address validation
    addressSuggestion,
    addressModalOpen,
    isValidatingAddress,
    setAddressModalOpen,
    handleAddressFieldBlur,
    handleAcceptAddressSuggestion,

    // Clinic field handler
    handleClinicFieldBlur,
  } = useCreateEpisodeStepForm();

  // ROC / SOC-existing modes. `kinnser_visit_type` is a SHARED key used by
  // both Kinnser and Roger scheduling (historical name).
  const visitType = (selectedIntake as { kinnser_visit_type?: string | null })
    ?.kinnser_visit_type;

  const isExistingPatient =
    (selectedIntake as { is_new_patient?: boolean | null })?.is_new_patient ===
    false;

  const isRocModeKinnser = pushToKinnser && visitType === 'roc';
  const isRocModeRoger =
    pushToRoger && isExistingPatient && visitType === 'roc';
  const isRocMode = isRocModeKinnser || isRocModeRoger;

  const isSocExistingPatientModeKinnser =
    pushToKinnser && isExistingPatient && visitType === 'soc';
  const isSocExistingPatientModeRoger =
    pushToRoger && isExistingPatient && visitType === 'soc';
  const isSocExistingPatientMode =
    isSocExistingPatientModeKinnser || isSocExistingPatientModeRoger;

  // SOC new admission + episode or new episode: auto_add (creates SOC) → doc upload → task details.
  const hasIntakeDocUploadFlag = hasFeatureFlag(FeatureFlag.INTAKE_DOC_UPLOAD);
  const creationType = (
    selectedIntake as { kinnser_creation_type?: string | null }
  )?.kinnser_creation_type as
    | 'new_admission'
    | 'new_episode'
    | null
    | undefined;
  const shouldCreateEpisode = !!(
    selectedIntake?.kinnser_create_episode ||
    selectedIntake?.roger_create_episode
  );
  const isSocNewAdmissionOrEpisodeMode =
    pushToKinnser &&
    shouldCreateEpisode &&
    hasIntakeDocUploadFlag &&
    (creationType === 'new_admission' || creationType === 'new_episode');
  const isNewPatient =
    (selectedIntake as { is_new_patient?: boolean | null })?.is_new_patient ===
    true;
  // New patients always use onboarding, not auto_add.
  const isSocNewAdmissionOrEpisodeModeForFlow =
    isSocNewAdmissionOrEpisodeMode && !isNewPatient;

  const kinnserTypeOfSoc = (
    selectedIntake as { kinnser_type_of_soc?: string | null }
  )?.kinnser_type_of_soc;
  const schedulingTabName: 'nursing' | 'pt' =
    kinnserTypeOfSoc === 'PT' ? 'pt' : 'nursing';

  // Unified flow for ROC and SOC existing: create task on existing episode, upload docs, update task details.
  const schedulingTaskName = isSocExistingPatientMode
    ? kinnserTypeOfSoc === 'PT'
      ? 'OASIS-E2 Start of Care (PT)'
      : 'OASIS-E2 Start of Care'
    : 'OASIS-E2 Resumption of Care';
  const rocDate = (selectedIntake as { roc_date?: string | null })?.roc_date;
  const socDateRaw = (selectedIntake as { kinnser_soc_date?: string | null })
    ?.kinnser_soc_date;
  const socDateOnly =
    socDateRaw && typeof socDateRaw === 'string'
      ? socDateRaw.split('T')[0]
      : null;
  const schedulingTaskDate = isSocExistingPatientMode
    ? socDateOnly
    : (rocDate ?? null);

  // Kinnser-specific match identity. Roger matches are represented by
  // `selectedIntake.patient_id` (see `hasMatchedRogerPatient` below); do not
  // conflate the two.
  const matchedKinnserPatientKey = (
    selectedIntake as { kinnser_matched_patient_key?: string | null }
  )?.kinnser_matched_patient_key?.trim();

  const { schedulingInKinnser, schedulingResult, handleScheduleInKinnser } =
    useKinnserScheduling({
      intakeId: selectedIntake?.id,
      intakeStatus: selectedIntake?.status,
      taskName: schedulingTaskName,
      tabName: schedulingTabName,
      taskDate: schedulingTaskDate,
    });

  // Roger: scheduling on an existing matched patient (SOC with new admission/episode, or ROC).
  // Unlike Kinnser which creates a task on an existing Kinnser episode, Roger needs to create
  // Admission/Episode/Visit rows in our own DB.
  const { schedulingInRoger, rogerSchedulingResult, handleScheduleInRoger } =
    useRogerScheduling({
      intakeId: selectedIntake?.id,
      intakeStatus: selectedIntake?.status,
    });

  const { autoAddInKinnser, autoAddResult, handleAutoAddInKinnser } =
    useKinnserAutoAddFlow({
      intakeId: selectedIntake?.id,
      intakeStatus: selectedIntake?.status,
      getValues: () => {
        const v = getValues() as unknown as {
          kinnser_soc_date?: string | null;
          kinnser_episode_start_date?: string | null;
          kinnser_clinician_id?: string | null;
        };
        const formClinician =
          v.kinnser_clinician_id &&
          typeof v.kinnser_clinician_id === 'string' &&
          v.kinnser_clinician_id.trim() !== ''
            ? v.kinnser_clinician_id.trim()
            : null;
        const intakeClinician =
          selectedIntake?.kinnser_clinician_id &&
          typeof selectedIntake.kinnser_clinician_id === 'string' &&
          selectedIntake.kinnser_clinician_id.trim() !== ''
            ? selectedIntake.kinnser_clinician_id.trim()
            : null;
        return {
          kinnser_soc_date: v.kinnser_soc_date ?? null,
          kinnser_episode_start_date: v.kinnser_episode_start_date ?? null,
          kinnser_clinician_id: formClinician ?? intakeClinician ?? null,
        };
      },
    });

  const shouldFetchKinnserClinicData =
    hasKinnserOption &&
    (pushToKinnser || selectedIntake?.push_to_kinnser === true);
  const { clinicData: schedulingClinicData } = useSchedulingClinicData({
    agencySystemName: user?.agency?.system_name,
    kinnserClinicId: selectedIntake?.kinnser_clinic_id,
    enabled: shouldFetchKinnserClinicData,
  });

  // Skip first run so we don't overwrite live validation left by other tabs (e.g. Scheduler) when switching to Push to EMR.
  // Re-run only when intake/schema changes (e.g. after save from another tab) so we have up-to-date state.
  const isFirstValidationRunRef = useRef(true);
  useEffect(() => {
    if (isFirstValidationRunRef.current) {
      isFirstValidationRunRef.current = false;
      return;
    }
    computeAndSetIntakeTabValidation(
      selectedIntake ?? null,
      intakeFormSchema ?? null,
      setIntakeTabValid
    );
  }, [selectedIntake, intakeFormSchema, setIntakeTabValid]);

  // Track if we've already initialized EMR selection for this intake
  const emrSelectionInitialized = useRef<string | null>(null);
  const [initializingEmrSelection, setInitializingEmrSelection] =
    useState(false);
  // Prevents duplicate confirmTabStatus calls when this effect re-runs before
  // onStatusChange() has refetched and updated tab_statuses to 'complete'.
  const hasAutoConfirmedPushToEmrRef = useRef(false);
  const handledFieldConflictRef = useRef<string | null>(null);
  const activeRogerCreateResult = pushToRoger ? rogerCreateResult : null;
  const rogerCreateResponse = activeRogerCreateResult?.response ?? null;
  const rogerFieldConflict = activeRogerCreateResult?.fieldConflict ?? null;

  // Reset auto-confirm tracking when switching to a different intake
  useEffect(() => {
    hasAutoConfirmedPushToEmrRef.current = false;
    handledFieldConflictRef.current = null;
  }, [selectedIntake?.id]);

  // Auto-mark Push to EMR tab complete when onboarding/create succeeds (or ROC/SOC scheduling or auto-add completed)
  useEffect(() => {
    const isAutoAddSuccess =
      isSocNewAdmissionOrEpisodeModeForFlow && autoAddResult?.success === true;
    const isKinnserScheduledSuccess =
      (isRocMode || isSocExistingPatientMode) &&
      pushToKinnser &&
      schedulingResult?.success === true;
    const isRogerScheduledSuccess =
      (isRocModeRoger || isSocExistingPatientModeRoger) &&
      pushToRoger &&
      rogerSchedulingResult?.success === true;
    const hasResult =
      createResult ||
      isKinnserScheduledSuccess ||
      isRogerScheduledSuccess ||
      isAutoAddSuccess;
    if (
      !selectedIntake?.id ||
      !hasResult ||
      hasAutoConfirmedPushToEmrRef.current
    )
      return;

    const isRogerOnboardSuccess =
      pushToRoger &&
      !isRocModeRoger &&
      !isSocExistingPatientModeRoger &&
      rogerCreateResponse?.success === true;
    const isKinnserSuccess =
      pushToKinnser &&
      !isRocMode &&
      !isSocExistingPatientMode &&
      (isSocNewAdmissionOrEpisodeModeForFlow
        ? autoAddResult?.success === true
        : isUsingOnboarding
          ? (createResult as KinnserOnboardPatientResponse)?.patient_creation
              ?.success === true
          : (createResult as KinnserCreatePatientResponse)?.success === true);
    const isSuccess =
      isRogerOnboardSuccess ||
      isKinnserSuccess ||
      isKinnserScheduledSuccess ||
      isRogerScheduledSuccess ||
      isAutoAddSuccess;
    const tabComplete =
      selectedIntake.tab_statuses?.push_to_emr === 'complete' ||
      selectedIntake.tab_statuses?.push_to_emr === 'complete_with_errors';
    if (!isSuccess || tabComplete) return;

    hasAutoConfirmedPushToEmrRef.current = true;
    confirmTabStatus(selectedIntake.id, 'push_to_emr', 'complete')
      .then(() => {
        onStatusChange?.();
      })
      .catch(() => {
        showError(
          'Failed to mark tab complete',
          'The Push to EMR tab could not be marked complete.'
        );
      });
  }, [
    selectedIntake?.id,
    selectedIntake?.tab_statuses?.push_to_emr,
    createResult,
    rogerCreateResponse?.success,
    schedulingResult,
    rogerSchedulingResult,
    autoAddResult,
    isRocMode,
    isRocModeRoger,
    isSocExistingPatientMode,
    isSocExistingPatientModeRoger,
    isSocNewAdmissionOrEpisodeModeForFlow,
    pushToRoger,
    pushToKinnser,
    isUsingOnboarding,
    confirmTabStatus,
    onStatusChange,
    showError,
  ]);

  useEffect(() => {
    if (!activeRogerCreateResult) {
      handledFieldConflictRef.current = null;
      return;
    }

    if (!onOnboardingFieldConflict) return;

    const fieldConflict = rogerFieldConflict;
    if (!fieldConflict) return;
    if (!selectedIntake?.id) return;

    const conflictKey = `${selectedIntake.id}:${fieldConflict.code}:${fieldConflict.field}`;
    if (handledFieldConflictRef.current === conflictKey) return;
    handledFieldConflictRef.current = conflictKey;

    void onOnboardingFieldConflict(fieldConflict);
  }, [
    onOnboardingFieldConflict,
    rogerFieldConflict,
    activeRogerCreateResult,
    selectedIntake?.id,
  ]);

  // Initialize EMR selection when entering the Push to EMR step
  // This auto-selects the EMR option if only one is enabled
  useEffect(() => {
    const initializeEmrSelection = async () => {
      if (!selectedIntake?.id) return;

      // Only initialize once per intake
      if (emrSelectionInitialized.current === selectedIntake.id) return;
      emrSelectionInitialized.current = selectedIntake.id;

      setInitializingEmrSelection(true);
      try {
        const response =
          await initializeEmrSelectionEndpointIntakeIntakeIdInitializeEmrSelectionPost(
            {
              path: { intake_id: selectedIntake.id },
              client,
            }
          );

        // Update form values if the backend auto-set the flags
        if (response.data) {
          if (
            response.data.push_to_roger !== selectedIntake.push_to_roger ||
            response.data.push_to_kinnser !== selectedIntake.push_to_kinnser
          ) {
            setValue('push_to_roger', response.data.push_to_roger ?? false);
            setValue('push_to_kinnser', response.data.push_to_kinnser ?? false);
          }
        }
      } catch (error) {
        // Silently fail if the API call fails
        console.error('Failed to initialize EMR selection:', error);
      } finally {
        setInitializingEmrSelection(false);
      }
    };

    initializeEmrSelection();
  }, [
    selectedIntake?.id,
    selectedIntake?.push_to_roger,
    selectedIntake?.push_to_kinnser,
    setValue,
  ]);

  // Check if user has permission to create patients
  if (!hasFeatureFlag(FeatureFlag.INTAKE_SCHEDULING_SUPPORT)) {
    return (
      <div className='p-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to create patients. Please contact your
            administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
      </div>
    );
  }

  const pushDisabledByIntakeState = !!(
    selectedIntake?.archived || selectedIntake?.non_admit
  );

  const persistedPushFailed = selectedIntake?.status === 'push_failed';
  const pushActionBlocked = persistedPushFailed;

  // Only Scheduling and Push to EMR tab block onboarding; other tabs (Extraction, Eligibility, etc.) do not.
  const TAB_LABELS: Record<IntakeTabValidationKey, string> = {
    [IntakeTabValidationKey.Extraction]: 'Extraction',
    [IntakeTabValidationKey.Eligibility]: 'Eligibility',
    [IntakeTabValidationKey.PriorAuth]: 'Prior Authorization',
    [IntakeTabValidationKey.ReadyToCall]: 'Ready to Call',
    [IntakeTabValidationKey.Scheduling]: 'Scheduling',
  };
  const tabsRequiredForPushToEmrGate: IntakeTabValidationKey[] = [
    // When visit type is ROC or SOC existing patient, Scheduling validations do not block the Push to EMR step.
    ...(hasSchedulingFeatureFlag && !isRocMode && !isSocExistingPatientMode
      ? [IntakeTabValidationKey.Scheduling]
      : []),
  ];

  // Scheduling tab uses clinic-aware validation (e.g. secondary scheduler required). On-load validation
  // uses the base schema without clinic context, so we re-check with clinic data so Push to EMR gate matches the tab.
  const schedulingValidFromClinicData = useMemo(
    () =>
      selectedIntake && schedulingClinicData != null
        ? validateSchedulingFromIntakeWithClinicData(
            selectedIntake,
            schedulingClinicData
          )
        : true,
    [selectedIntake, schedulingClinicData]
  );

  const isTabValidForGate = (tab: IntakeTabValidationKey): boolean => {
    if (tab === IntakeTabValidationKey.Scheduling) {
      return intakeTabValidation[tab] === true && schedulingValidFromClinicData;
    }
    return intakeTabValidation[tab] === true;
  };

  const priorTabsValid = tabsRequiredForPushToEmrGate.every(isTabValidForGate);
  const incompleteStepNames = tabsRequiredForPushToEmrGate
    .filter(tab => !isTabValidForGate(tab))
    .map(tab => TAB_LABELS[tab] ?? tab);

  const isLoading =
    loadingClinics || loadingClinicData || initializingEmrSelection;

  // Existing patient is confirmed when the intake has a persisted matched patient:
  //   - Kinnser: kinnser_matched_patient_key (Extraction or Push-to-EMR match modal)
  //   - Roger:   intake.patient_id linked to an existing Roger patient
  const hasMatchedRogerPatient =
    isExistingPatient && !!selectedIntake?.patient_id;
  const rocHasSelectedPatient =
    !!matchedKinnserPatientKey || hasMatchedRogerPatient;
  const rocVisitTypeLabel =
    (selectedIntake as { kinnser_visit_type?: string | null })
      ?.kinnser_visit_type === 'roc'
      ? 'ROC'
      : ((selectedIntake as { kinnser_visit_type?: string | null })
          ?.kinnser_visit_type ?? 'visit');
  const allTabsValid =
    isRocMode ||
    isSocExistingPatientMode ||
    isSocNewAdmissionOrEpisodeModeForFlow
      ? priorTabsValid && rocHasSelectedPatient
      : isValid && priorTabsValid;

  // Scheduling flows (matched patient, no new-patient creation):
  //   - Kinnser ROC / SOC-existing: task on existing Kinnser episode (handleScheduleInKinnser)
  //   - Roger   ROC / SOC-existing: create Admission/Episode/Visit on existing Roger patient
  //     (handleScheduleInRoger)
  // SOC new-admission/episode on existing Kinnser patient uses the auto-add flow; new patients
  // always go through standard onboarding/creation.
  const visitScheduleHandler = isSocNewAdmissionOrEpisodeModeForFlow
    ? handleAutoAddInKinnser
    : (isRocMode || isSocExistingPatientMode) && pushToKinnser
      ? handleScheduleInKinnser
      : (isRocModeRoger || isSocExistingPatientModeRoger) && pushToRoger
        ? handleScheduleInRoger
        : undefined;
  const creatingForButton = isSocNewAdmissionOrEpisodeModeForFlow
    ? creatingInEMR || autoAddInKinnser
    : (isRocMode || isSocExistingPatientMode) && pushToKinnser
      ? creatingInEMR || schedulingInKinnser
      : (isRocModeRoger || isSocExistingPatientModeRoger) && pushToRoger
        ? creatingInEMR || schedulingInRoger
        : creatingInEMR;
  const suppressRogerPushError =
    pushToRoger &&
    !!onOnboardingFieldConflict &&
    shouldSuppressRogerOnboardingPushError(activeRogerCreateResult);
  const visibleCreateResult: DisplayCreateResult = suppressRogerPushError
    ? null
    : pushToRoger
      ? rogerCreateResponse
      : (createResult as
          | KinnserCreatePatientResponse
          | KinnserOnboardPatientResponse
          | null);
  const displayCreateResult =
    isSocNewAdmissionOrEpisodeModeForFlow && autoAddResult != null
      ? ({
          success: autoAddResult.success,
          message: autoAddResult.message,
          ...('patient_task_key' in autoAddResult &&
          autoAddResult.patient_task_key
            ? {
                patient_task_key: autoAddResult.patient_task_key,
              }
            : {}),
          task_name:
            creationType === 'new_admission'
              ? 'OASIS-E2 Start of Care (new admission)'
              : 'OASIS-E2 Start of Care (new episode)',
        } as KinnserCreatePatientResponse)
      : (isRocMode || isSocExistingPatientMode) &&
          pushToKinnser &&
          schedulingResult != null
        ? ({
            success: schedulingResult.success,
            message: schedulingResult.message,
            ...('patient_task_key' in schedulingResult &&
            schedulingResult.patient_task_key
              ? {
                  patient_task_key: schedulingResult.patient_task_key,
                }
              : {}),
            ...('task_name' in schedulingResult && schedulingResult.task_name
              ? { task_name: schedulingResult.task_name }
              : {}),
          } as KinnserCreatePatientResponse)
        : (isRocModeRoger || isSocExistingPatientModeRoger) &&
            pushToRoger &&
            rogerSchedulingResult != null
          ? rogerSchedulingResult
          : visibleCreateResult;

  return (
    <>
      <div className='h-full flex flex-col'>
        <Card className='h-full flex flex-col'>
          <CardHeader className='border-b py-3 px-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                {saveStatus === 'saving' && (
                  <div className='flex items-center gap-2 text-xs text-gray-500'>
                    <Loader2 className='h-3 w-3 animate-spin' />
                    <span>Saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div className='text-xs text-green-600'>Saved</div>
                )}
                {saveStatus === 'error' && (
                  <div className='text-xs text-red-600'>Error saving</div>
                )}
                {pushDisabledByIntakeState && (
                  <Alert className='py-2 px-3 bg-amber-50 border-amber-200'>
                    <AlertDescription className='text-amber-800 text-xs flex gap-2 items-center'>
                      <AlertTriangle className='h-4 w-4 flex-shrink-0' />
                      <span>
                        Push to EMR is not available for archived or non-admit
                        intakes.
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
                {persistedPushFailed && (
                  <Alert className='py-2 px-3 bg-red-50 border-red-200'>
                    <AlertDescription className='text-red-800 text-xs flex gap-2 items-center'>
                      <AlertTriangle className='h-4 w-4 flex-shrink-0' />
                      <span>
                        Push to EMR failed for this intake. The Roger team has
                        been notified.
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              <div className='ml-4 flex flex-row flex-nowrap items-center gap-2 shrink-0'>
                <PatientCreateButton
                  handleCreate={
                    pushToRoger
                      ? (visitScheduleHandler ?? handleCreateInRoger)
                      : pushToKinnser
                        ? (visitScheduleHandler ?? handleCreateInKinnser)
                        : async () => {}
                  }
                  onComplete={onComplete}
                  creating={creatingForButton}
                  saveStatus={saveStatus}
                  loadingClinics={loadingClinics}
                  loadingClinicData={loadingClinicData}
                  selectedIntake={selectedIntake}
                  isUsingOnboarding={isUsingOnboarding}
                  preferOnboardLabel={Boolean(
                    (isSocNewAdmissionOrEpisodeMode || isNewPatient) &&
                      pushToKinnser
                  )}
                  isExistingPatient={!isNewPatient}
                  pushToEmrTabComplete={
                    selectedIntake.tab_statuses?.push_to_emr === 'complete' ||
                    selectedIntake.tab_statuses?.push_to_emr ===
                      'complete_with_errors'
                  }
                  createResult={displayCreateResult}
                  isValid={allTabsValid}
                  pushToRoger={pushToRoger}
                  pushToKinnser={pushToKinnser}
                  pushDisabledByIntakeState={pushDisabledByIntakeState}
                  disableAfterFailure={pushActionBlocked}
                  mode={
                    isRocMode
                      ? 'roc'
                      : isSocExistingPatientMode ||
                          (isExistingPatient && (pushToKinnser || pushToRoger))
                        ? 'soc_existing'
                        : 'create'
                  }
                  hasSelectedPatient={rocHasSelectedPatient}
                  className='shrink-0'
                />
                {selectedIntake && (
                  <MarkCompletedButton
                    intakeId={selectedIntake.id}
                    tabKey='push_to_emr'
                    isComplete={
                      selectedIntake.tab_statuses?.push_to_emr === 'complete' ||
                      selectedIntake.tab_statuses?.push_to_emr ===
                        'complete_with_errors'
                    }
                    onStatusChange={onStatusChange}
                    disabled={creatingForButton || pushDisabledByIntakeState}
                    completeButtonLabel={
                      isRocMode ||
                      isSocExistingPatientMode ||
                      isSocNewAdmissionOrEpisodeMode ||
                      isNewPatient
                        ? 'Mark Complete'
                        : 'Mark Complete without Onboarding'
                    }
                    className='shrink-0'
                  />
                )}
              </div>
            </div>

            {/* Red validation warning: tab(s) to complete (e.g. Scheduling for new patients) and required fields on this tab.
                In ROC or SOC existing patient mode, Scheduling does not block so we skip; for new patients we show this so they fix Scheduling first.
                After a push failure, hide required-field hints—they are misleading while the action is blocked. */}
            {!isLoading &&
              !isRocMode &&
              !isSocExistingPatientMode &&
              (incompleteStepNames.length > 0 ||
                (Object.keys(errors).length > 0 && !pushActionBlocked)) && (
                <Alert className='mt-3 py-2 px-3 bg-red-50 border-red-200 text-red-900 text-xs'>
                  <div className='flex items-start gap-2'>
                    <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-red-600' />
                    <div className='flex-1 space-y-1 text-red-800'>
                      {incompleteStepNames.length > 0 && (
                        <>
                          <p className='font-medium'>
                            Additional tabs to complete:
                          </p>
                          <ul
                            className='list-disc list-inside space-y-0.5'
                            role='list'
                            aria-label='Tabs to complete'
                          >
                            {incompleteStepNames.map(name => (
                              <li key={name}>{name}</li>
                            ))}
                          </ul>
                        </>
                      )}
                      {Object.keys(errors).length > 0 && !pushActionBlocked && (
                        <>
                          <p className='font-medium'>
                            Required fields on this tab:
                          </p>
                          <ul
                            className='list-disc list-inside space-y-0.5'
                            role='list'
                            aria-label='Required fields'
                          >
                            <li>
                              {Object.entries(errors)
                                .map(([, error]) => {
                                  const message =
                                    (error as { message?: string })?.message ||
                                    '';
                                  return message.replace(/ is required$/, '');
                                })
                                .filter(Boolean)
                                .join(', ')}
                            </li>
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </Alert>
              )}
            {/* Validation errors or success message */}
            {(visibleCreateResult ||
              (isSocNewAdmissionOrEpisodeModeForFlow && autoAddResult) ||
              ((isRocMode || isSocExistingPatientMode) &&
                pushToKinnser &&
                schedulingResult) ||
              ((isRocModeRoger || isSocExistingPatientModeRoger) &&
                pushToRoger &&
                rogerSchedulingResult)) && (
              <div className='mt-3'>
                {isSocNewAdmissionOrEpisodeModeForFlow && autoAddResult ? (
                  autoAddResult.success ? (
                    <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                      <div className='flex items-center gap-2 text-green-800'>
                        <CheckCircle className='h-4 w-4' />
                        <span className='font-medium'>
                          {creationType === 'new_admission'
                            ? 'New admission and episode created in Kinnser'
                            : 'New episode created in Kinnser'}
                        </span>
                      </div>
                      <div className='text-sm text-green-700 mt-2'>
                        {autoAddResult.message}
                      </div>
                      {'patient_task_key' in autoAddResult &&
                        autoAddResult.patient_task_key && (
                          <div className='text-sm text-green-700 mt-1'>
                            Task ({autoAddResult.patient_task_key})
                          </div>
                        )}
                    </div>
                  ) : (
                    (() => {
                      const genericAutoAddFailure =
                        typeof autoAddResult.message === 'string' &&
                        autoAddResult.message.trim() ===
                          'Auto-add failed. Please try again.';
                      if (persistedPushFailed && genericAutoAddFailure) {
                        return null;
                      }
                      return (
                        <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                          <div className='flex items-center gap-2 text-red-800 mb-2'>
                            <XCircle className='h-4 w-4' />
                            <span className='font-medium'>
                              {creationType === 'new_admission'
                                ? 'New admission and episode'
                                : 'New episode'}{' '}
                              incomplete
                            </span>
                          </div>
                          <div className='text-sm text-red-700'>
                            {autoAddResult.message}
                          </div>
                        </div>
                      );
                    })()
                  )
                ) : (isRocMode || isSocExistingPatientMode) &&
                  pushToKinnser &&
                  schedulingResult ? (
                  schedulingResult.success ? (
                    <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                      <div className='flex items-center gap-2 text-green-800'>
                        <CheckCircle className='h-4 w-4' />
                        <span className='font-medium'>
                          {isSocExistingPatientMode ? 'SOC' : 'ROC'} scheduled
                          on existing episode in Kinnser
                        </span>
                      </div>
                      <div className='text-sm text-green-700 mt-2'>
                        {schedulingResult.message}
                      </div>
                      {'patient_task_key' in schedulingResult &&
                        schedulingResult.patient_task_key && (
                          <div className='text-sm text-green-700 mt-1'>
                            Task:{' '}
                            {('task_name' in schedulingResult &&
                              schedulingResult.task_name) ??
                              schedulingTaskName}{' '}
                            ({schedulingResult.patient_task_key})
                          </div>
                        )}
                    </div>
                  ) : (
                    (() => {
                      const genericSchedulingFailure =
                        typeof schedulingResult.message === 'string' &&
                        schedulingResult.message.trim() ===
                          'Scheduling failed. Please try again.';
                      if (persistedPushFailed && genericSchedulingFailure) {
                        return null;
                      }
                      return (
                        <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                          <div className='flex items-center gap-2 text-red-800 mb-2'>
                            <XCircle className='h-4 w-4' />
                            <span className='font-medium'>
                              {isSocExistingPatientMode ? 'SOC' : 'ROC'}{' '}
                              scheduling incomplete
                            </span>
                          </div>
                          <div className='text-sm text-red-700'>
                            {schedulingResult.message}
                          </div>
                        </div>
                      );
                    })()
                  )
                ) : pushToRoger ? (
                  (() => {
                    const isRogerSchedulingMode =
                      isRocModeRoger || isSocExistingPatientModeRoger;
                    const rogerResult = (
                      isRogerSchedulingMode
                        ? rogerSchedulingResult
                        : visibleCreateResult
                    ) as RogerOnboardPatientResponse | null;
                    if (!rogerResult) return null;
                    const successHeader = isRogerSchedulingMode
                      ? `${isRocModeRoger ? 'ROC' : 'SOC'} scheduled on existing Roger patient`
                      : isUsingOnboarding
                        ? 'Patient, episode, task, and attachment created in Roger'
                        : 'Patient created successfully in Roger';
                    const failureHeader = isRogerSchedulingMode
                      ? `${isRocModeRoger ? 'ROC' : 'SOC'} scheduling incomplete`
                      : isUsingOnboarding
                        ? 'Onboarding Failed'
                        : 'Validation Errors';
                    return rogerResult.success ? (
                      <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                        <div className='flex items-center justify-between text-green-800'>
                          <div className='flex items-center gap-2'>
                            <CheckCircle className='h-4 w-4' />
                            <span className='font-medium'>{successHeader}</span>
                          </div>
                          {rogerResult.patient_id && (
                            <a
                              href={`/patients/${rogerResult.patient_id}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='flex items-center gap-1 text-sm font-medium text-green-700 hover:text-green-900 underline'
                            >
                              View Patient
                              <ExternalLink className='h-3 w-3' />
                            </a>
                          )}
                        </div>
                        {rogerResult.message && (
                          <div className='text-sm text-green-700 mt-2'>
                            {rogerResult.message}
                          </div>
                        )}
                      </div>
                    ) : (
                      (() => {
                        const genericOnboardFailure =
                          typeof rogerResult.message === 'string' &&
                          rogerResult.message.trim() ===
                            'Failed to onboard patient. Please try again.';
                        if (
                          persistedPushFailed &&
                          isUsingOnboarding &&
                          genericOnboardFailure
                        ) {
                          return null;
                        }
                        return (
                          <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                            {isUsingOnboarding &&
                            !isRogerSchedulingMode ? null : (
                              <div className='flex items-center gap-2 text-red-800 mb-2'>
                                <XCircle className='h-4 w-4' />
                                <span className='font-medium'>
                                  {failureHeader}
                                </span>
                              </div>
                            )}
                            <div className='text-sm text-red-700'>
                              {rogerResult.message}
                            </div>
                          </div>
                        );
                      })()
                    );
                  })()
                ) : isUsingOnboarding ? (
                  // Onboarding result display
                  (createResult as KinnserOnboardPatientResponse)
                    .patient_creation.success ? (
                    <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                      <div className='flex items-center gap-2 text-green-800'>
                        <CheckCircle className='h-4 w-4' />
                        <span className='font-medium'>
                          Patient, episode, task, and attachment created in
                          Kinnser
                        </span>
                      </div>
                      <div className='text-sm text-green-700 mt-2 space-y-1'>
                        {(createResult as KinnserOnboardPatientResponse)
                          .task_creation.patient_task_key && (
                          <div>
                            Task:{' '}
                            {
                              (createResult as KinnserOnboardPatientResponse)
                                .task_creation.task_name
                            }{' '}
                            (
                            {
                              (createResult as KinnserOnboardPatientResponse)
                                .task_creation.patient_task_key
                            }
                            )
                          </div>
                        )}
                        {(createResult as KinnserOnboardPatientResponse).upload
                          .attachments && (
                          <div>
                            Uploaded:{' '}
                            {(
                              createResult as KinnserOnboardPatientResponse
                            ).upload.attachments
                              .map(
                                att =>
                                  att.client_file ||
                                  att.server_file ||
                                  'Unknown'
                              )
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    (() => {
                      const msg = (
                        createResult as KinnserOnboardPatientResponse
                      ).patient_creation.message;
                      const genericOnboardFailure =
                        typeof msg === 'string' &&
                        msg.trim() ===
                          'Failed to onboard patient. Please try again.';
                      if (persistedPushFailed && genericOnboardFailure) {
                        return null;
                      }
                      return (
                        <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                          <div className='text-sm text-red-700'>{msg}</div>
                        </div>
                      );
                    })()
                  )
                ) : // Creation result display
                (createResult as KinnserCreatePatientResponse).success ? (
                  <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                    <div className='flex items-center gap-2 text-green-800'>
                      <CheckCircle className='h-4 w-4' />
                      <span className='font-medium'>
                        Patient created successfully in Kinnser
                      </span>
                    </div>
                    {(createResult as KinnserCreatePatientResponse)
                      .kinnser_patient_id && (
                      <div className='text-sm text-green-700 mt-1'>
                        Kinnser Patient ID:{' '}
                        {
                          (createResult as KinnserCreatePatientResponse)
                            .kinnser_patient_id
                        }
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                    <div className='flex items-center gap-2 text-red-800 mb-2'>
                      <XCircle className='h-4 w-4' />
                      <span className='font-medium'>Validation Errors</span>
                    </div>
                    <ul className='list-disc list-inside text-sm text-red-700 space-y-1'>
                      {(
                        createResult as KinnserCreatePatientResponse
                      ).errors?.map((error, index) => (
                        <li key={index}>{error.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
            {isLoading ? (
              <div className='h-full flex items-center justify-center'>
                <div className='flex flex-col items-center gap-4'>
                  <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
                </div>
              </div>
            ) : (isRocMode || isSocExistingPatientMode) &&
              rocHasSelectedPatient ? (
              <div className='flex flex-col items-center h-full py-12 px-4 text-center'>
                <div className='bg-gray-50 p-4 rounded-full mb-4'>
                  <CalendarDays className='h-8 w-8 text-gray-400' />
                </div>
                <h3 className='text-lg font-medium text-gray-900'>
                  Schedule{' '}
                  {isSocExistingPatientMode ? 'SOC' : rocVisitTypeLabel} in{' '}
                  {pushToRoger ? 'Roger' : 'Kinnser'}
                </h3>
                <p className='text-sm text-gray-500 mt-1 max-w-sm'>
                  The patient has been matched. Continue to schedule the visit
                  in {pushToRoger ? 'Roger' : 'Kinnser'}.
                </p>
              </div>
            ) : isRocMode || isSocExistingPatientMode ? (
              <div className='flex flex-col items-center h-full py-12 px-4 text-center'>
                <div className='bg-gray-50 p-4 rounded-full mb-4'>
                  <CalendarDays className='h-8 w-8 text-gray-400' />
                </div>
                <h3 className='text-lg font-medium text-gray-900'>
                  Select patient on Extraction
                </h3>
                <p className='text-sm text-gray-500 mt-1 max-w-sm'>
                  Go to the Extraction step to select an existing patient, then
                  return here to schedule the visit in{' '}
                  {pushToRoger ? 'Roger' : 'Kinnser'}.
                </p>
              </div>
            ) : isExistingPatient && (pushToRoger || pushToKinnser) ? (
              <div className='flex flex-col items-center h-full py-12 px-4 text-center'>
                <div className='bg-gray-50 p-4 rounded-full mb-4'>
                  <CalendarDays className='h-8 w-8 text-gray-400' />
                </div>
                <h3 className='text-lg font-medium text-gray-900'>
                  Schedule on Scheduling tab
                </h3>
                <p className='text-sm text-gray-500 mt-1 max-w-sm'>
                  This patient is matched as existing. Use the Scheduling tab to
                  schedule the visit; no fields are required on this tab.
                </p>
              </div>
            ) : pushToRoger ? (
              <RogerCreateEpisodeStepFormContent
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                displayValues={{
                  hhCahpsOptOut: hhCahpsOptOut || false,
                }}
                clinics={clinics}
                clinicData={clinicData}
                loadingClinics={loadingClinics}
                loadingClinicData={loadingClinicData}
                insuranceOptions={insuranceOptions}
                selectedIntake={selectedIntake}
                isValidatingAddress={isValidatingAddress}
                handleAddressFieldBlur={handleAddressFieldBlur}
                kinnserLanguageKey={kinnserLanguageKey || undefined}
                rogerPhysicianOptions={rogerPhysicianOptions}
                loadingPhysicians={loadingPhysicians}
              />
            ) : pushToKinnser ? (
              <KinnserCreateEpisodeStepFormContent
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                handleFieldBlur={handleFieldBlur}
                displayValues={{
                  hhCahpsOptOut: hhCahpsOptOut || false,
                  kinnserLanguageKey: kinnserLanguageKey || undefined,
                }}
                saveStatus={saveStatus}
                saveToServer={saveToServer}
                clinics={clinics}
                clinicData={clinicData}
                loadingClinics={loadingClinics}
                loadingClinicData={loadingClinicData}
                insuranceOptions={insuranceOptions}
                selectedIntake={selectedIntake}
                isValidatingAddress={isValidatingAddress}
                handleAddressFieldBlur={handleAddressFieldBlur}
                kinnserLanguageKey={kinnserLanguageKey}
                isFormValid={isValid}
                addressSuggestion={addressSuggestion}
                addressModalOpen={addressModalOpen}
                setAddressModalOpen={setAddressModalOpen}
                handleAcceptAddressSuggestion={handleAcceptAddressSuggestion}
                handleClinicFieldBlur={handleClinicFieldBlur}
              />
            ) : (
              <div className='h-full flex justify-center'>
                <div className='text-center max-w-md flex flex-col items-center'>
                  <AlertTriangle className='h-12 w-12 text-yellow-500 mb-4' />
                  <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                    No Selection Made
                  </h3>
                  <p className='text-gray-500 mb-6'>
                    Toggle one of the checkboxes above to continue
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AddressSuggestionModal
        addressModalOpen={addressModalOpen}
        setAddressModalOpen={setAddressModalOpen}
        addressSuggestion={addressSuggestion}
        handleAcceptAddressSuggestion={handleAcceptAddressSuggestion}
      />
    </>
  );
};

export default CreateEpisodeStep;
